<template>
  <div class="hold-page">
    <AppHeader
      title="Rapport na betaling"
      :show-back-button="true"
      back-to="/rapporten"
      back-text="Terug naar rapporten"
    />

    <section class="hold-card">
      <p class="hold-label">{{ isPaymentHold ? "Beschikbaar na betaling" : "Nog niet beschikbaar" }}</p>
      <h2 class="hold-title">
        {{
          isPaymentHold
            ? `${inspectieLabel || "Het rapport"} staat voor u klaar`
            : `${inspectieLabel || "Het rapport"} is nog niet vrijgegeven`
        }}
      </h2>
      <p
        v-if="isPaymentHold"
        class="hold-description"
      >
        Dit rapport wordt vrijgegeven zodra de bijbehorende factuur is betaald. Na ontvangst van uw betaling
        ontvangt u het rapport automatisch per e-mail en is het hier direct beschikbaar.
      </p>
      <p
        v-else
        class="hold-description"
      >
        Het inspectiebedrijf heeft dit rapport nog niet vrijgegeven. Zodra het rapport beschikbaar is, ontvangt u
        het per e-mail en kunt u het hier bekijken.
      </p>

      <div v-if="hold?.factuurnr" class="hold-factuur">
        <div class="hold-factuur-row">
          <span>Factuur</span>
          <strong>{{ hold.factuurnr }}</strong>
        </div>
        <div v-if="hold.factuur_totaal !== null" class="hold-factuur-row">
          <span>Bedrag</span>
          <strong>{{ formatCurrency(hold.factuur_totaal) }}</strong>
        </div>
        <div v-if="hold.factuur_vervaldatum" class="hold-factuur-row">
          <span>Vervaldatum</span>
          <strong>{{ formatDate(hold.factuur_vervaldatum) }}</strong>
        </div>
      </div>

      <div class="hold-actions">
        <Button
          v-if="hold?.payment_link"
          label="Betaal nu"
          icon="pi pi-credit-card"
          @click="openPaymentLink"
        />
        <Button
          v-if="hold?.factuur_id"
          label="Bekijk factuur"
          severity="secondary"
          :outlined="true"
          @click="router.push(`/factuur/${hold.factuur_id}`)"
        />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import Button from "primevue/button";
  import AppHeader from "../components/AppHeader.vue";
  import { useRapportPaymentHolds } from "../composables/useRapportPaymentHolds";
  import { getInspectieTypeParam } from "../utils/rapportScope";
  import { inspectieTypeLabels, type RapportPaymentHold } from "../../../../types";

  const route = useRoute();
  const router = useRouter();
  const { fetchRapportPaymentHold } = useRapportPaymentHolds();

  const hold = ref<RapportPaymentHold | null>(null);

  // Payment copy only when an unpaid rapport_na_betaling factuur is the reason;
  // otherwise the rapport simply hasn't been released yet.
  const isPaymentHold = computed(() => Boolean(hold.value?.held));

  const rapportId = computed(() => (typeof route.query.id === "string" ? route.query.id : ""));
  const inspectieType = computed(() => getInspectieTypeParam(route.query.type));
  const inspectieLabel = computed(() => {
    if (!inspectieType.value) return "";
    return inspectieTypeLabels[inspectieType.value] || inspectieType.value;
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(value);
  const formatDate = (value: string) => new Intl.DateTimeFormat("nl-NL").format(new Date(value));

  const openPaymentLink = () => {
    if (hold.value?.payment_link) {
      window.open(hold.value.payment_link, "_blank");
    }
  };

  onMounted(async () => {
    if (!rapportId.value) return;
    try {
      const result = await fetchRapportPaymentHold(rapportId.value);
      // The release can happen between navigation and this fetch (payment just
      // came in, or the ibedrijf released manually): send the user straight
      // through to the rapport.
      if (result?.zichtbaar) {
        await router.replace(`/rapport/${rapportId.value}`);
        return;
      }
      hold.value = result;
    } catch (error) {
      console.error("Kon betaalstatus niet laden:", error);
    }
  });
</script>

<style scoped>
  .hold-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-bottom: 2rem;
  }

  .hold-card {
    border-radius: 0.75rem;
    border: 1px solid var(--p-background-200);
    background: linear-gradient(145deg, var(--p-background-0), var(--p-background-50));
    padding: 2rem;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
    max-width: 48rem;
  }

  .hold-label {
    margin: 0 0 0.75rem;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--p-primary-500, #3b82f6);
  }

  .hold-title {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--p-background-900);
  }

  .hold-description {
    margin: 1rem 0 0;
    line-height: 1.7;
    color: var(--p-background-700);
    max-width: 40rem;
  }

  .hold-factuur {
    margin-top: 1.5rem;
    max-width: 24rem;
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .hold-factuur-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    font-size: 0.9rem;
    color: var(--p-background-700);
  }

  .hold-actions {
    margin-top: 1.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
</style>
